/**
 * Provider base abstracto para transacciones
 */

import { Transaction, TransactionProvider } from '@/types/transaction.types';

export abstract class BaseTransactionProvider implements TransactionProvider {
  protected network: string;
  protected timeout: number;

  constructor(network: string, timeout = 5000) {
    this.network = network;
    this.timeout = timeout;
  }

  abstract getTransactions(address: string, fromBlock?: number): Promise<Transaction[]>;
  abstract getTokenTransfers(address: string, contractAddress?: string): Promise<Transaction[]>;
  abstract getTransactionDetails(txHash: string): Promise<Transaction | null>;

  /**
   * Realiza una petición HTTP con timeout y manejo de errores
   */
  protected async fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Content-Type solo tiene sentido si hay body (POST con JSON, ej. Alchemy).
      // En un GET simple sin headers custom, agregarlo igual convierte la
      // request en "no simple" y fuerza un preflight CORS — varios explorers
      // (ej. omdbscan.com) 301-redirigen /api?... a /api/?..., y los navegadores
      // rechazan seguir redirects en un preflight, rompiendo el GET entero.
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${this.timeout}ms`);
        }
        throw error;
      }
      
      throw new Error('Unknown error occurred');
    }
  }

  /**
   * Convierte timestamp a número
   */
  protected parseTimestamp(timestamp: string | number): number {
    if (typeof timestamp === 'number') return timestamp * 1000;
    const parsed = parseInt(timestamp);
    return parsed * 1000; // Convertir a milisegundos
  }

  /**
   * Convierte valor de wei a ether
   */
  protected parseValue(value: string | number, decimals = 18): string {
    if (typeof value === 'number') value = value.toString();
    
    // Si ya contiene un punto decimal, asumimos que ya está en la unidad correcta
    if (value.includes('.')) {
      return value;
    }

    try {
      const valueNum = BigInt(value);
      const divisor = BigInt(10) ** BigInt(decimals);
      const result = valueNum / divisor;
      const remainder = valueNum % divisor;
      
      if (remainder === 0n) {
        return result.toString();
      }
      
      const remainderStr = remainder.toString().padStart(decimals, '0');
      const trimmedRemainder = remainderStr.replace(/0+$/, '');
      
      if (trimmedRemainder === '') {
        return result.toString();
      }
      
      return `${result}.${trimmedRemainder}`;
    } catch (e) {
      // Si no es un BigInt válido (ej. notación científica), devolvemos el string original
      return value;
    }
  }

  /**
   * Determina el tipo de transacción
   */
  protected getTransactionType(from: string, _to: string, userAddress: string): 'send' | 'receive' {
    return from.toLowerCase() === userAddress.toLowerCase() ? 'send' : 'receive';
  }

  /**
   * Maneja errores de rate limiting
   */
  protected async handleRateLimit(error: Error): Promise<void> {
    if (error.message.includes('429') || error.message.includes('rate limit')) {
      console.warn(`[${this.network}] Rate limit reached, waiting 60 seconds...`);
      await new Promise(resolve => setTimeout(resolve, 60000));
    }
  }
}